/**
 * The client side of a GPT-Live call (plan 042): a mic stream, a peer
 * connection with the `oai-events` data channel, and the function-call loop
 * that delegates `magnus_chat`. The server mints the session (it holds the
 * OpenAI key); this store only exchanges SDP through `mint`. The mic, peer
 * connection and audio come from a `LiveCallPlatform` (the browser's by
 * default). Framework-agnostic — `createMagnusLiveCall` drives it on web,
 * in the SDK and on mobile.
 */

import type {
  VoiceSessionEndReason,
  VoiceSessionTurn,
} from "@magnus/contract/messages";
import type {
  SdkLiveSessionAnswer,
  SdkLiveSessionRequest,
} from "@magnus/contract/voice";
import {
  CANCEL_INTENT,
  commentaryCommand,
  createLiveFunctionCallLoop,
  inputAudioMuteCommand,
  LIVE_CALL_IDLE_MS,
  LIVE_CALL_MAX_MS,
  LIVE_DATA_CHANNEL,
  type LiveCommand,
  type LiveEvent,
  type LiveVoice,
  MAGNUS_CHAT_FUNCTION,
  parseLiveEvent,
  thinkingCommand,
} from "@workspace/voice-shared/live";
import {
  callTranscript,
  type LiveCallPhase,
} from "@workspace/voice-shared/live-call";
import {
  createMagnusChatToolRunner,
  type MagnusChatDelegate,
  recentCallContext,
} from "@workspace/voice-shared/magnus-chat-tool";
import type { SdkRestResult } from "../../rest";
import { browserLiveCallPlatform } from "./browser-platform";
import { type Call, newCall, saidSince } from "./call-session";
import { closeLeg, timeZone, waitForIceGathering } from "./leg";
import {
  BROWSER_MESSAGES,
  CALL_DROPPED_MESSAGE,
  sessionErrorMessage,
  startErrorMessage,
} from "./messages";
import type {
  AudioInputDevice,
  LiveCallDataChannel,
  LiveCallLog,
  LiveCallPeerConnection,
  LiveCallPlatform,
  LiveCallStream,
} from "./platform";
import { switchCallVoice } from "./switch-voice";

export {
  LIVE_CALL_DISABLED_MESSAGE,
  LIVE_CALL_UNCONFIGURED_MESSAGE,
} from "./messages";
export type { AudioInputDevice } from "./platform";

export const consoleLiveCallLog: LiveCallLog = (level, message, detail) => {
  console[level](`[voice-live] ${message}`, detail);
};

export type LiveCallEnd = {
  sessionId: string;
  callId?: string;
  startedAt: string;
  endedAt: string;
  endReason: VoiceSessionEndReason;
  turns: VoiceSessionTurn[];
  unloading: boolean;
};

export type LiveCallStartResult =
  | "started"
  | "unconfigured"
  | "disabled"
  | "failed";

export type LiveCaptions = { user: string; assistant: string };

export type LiveCallState = {
  phase: LiveCallPhase;
  error: string | null;
  sessionId: string | null;
  startedAt: string | null;
  inputLevel: number;
  outputLevel: number;
  isMuted: boolean;
  inputDevices: AudioInputDevice[];
  inputDeviceId: string | null;
  captions: LiveCaptions;
  delegatingQuery: string | null;
};

/** Exchanges the client's SDP offer for the provider's answer. */
export type LiveCallMint = (
  input: SdkLiveSessionRequest,
  options?: { signal?: AbortSignal }
) => Promise<SdkRestResult<SdkLiveSessionAnswer>>;

export type LiveCallHandlers = {
  delegate: MagnusChatDelegate;
  onReady?: () => void;
  onCallEnd: (end: LiveCallEnd) => void;
  onCancel?: () => void;
  /** A mint answered after its call had ended, so the server holds a lease
   *  the end's release may have raced. */
  onLateSession?: (callSessionId: string) => void;
};

export type LiveCallStopOptions = {
  /** The page is going away: no graceful close, keepalive saves. */
  unloading?: boolean;
  /** Default `user`; `interrupted` when the OS or another app ended it. */
  reason?: Extract<VoiceSessionEndReason, "user" | "interrupted">;
};

export interface LiveCallStore {
  /** Ends a live call as an unload (no graceful close) and stops watching
   *  devices. Subscribers stay; the store stays usable, so a StrictMode
   *  re-mount can start again. */
  dispose(): void;
  getState(): LiveCallState;
  /** Facts for the voice model to draw on later; each note is one append. */
  pushContext(notes: readonly string[]): void;
  setHandlers(handlers: LiveCallHandlers): void;
  setInputDevice(deviceId: string | null): Promise<void>;
  setMuted(muted: boolean): void;
  start(ctx: {
    chatId?: string;
    voice: LiveVoice;
  }): Promise<LiveCallStartResult>;
  stop(opts?: LiveCallStopOptions): void;
  subscribe(listener: () => void): () => void;
  /** Replaces the provider session mid-call, carrying the transcript over. */
  switchVoice(voice: LiveVoice): Promise<boolean>;
}

const TICK_MS = 60;
const SESSION_START_TIMEOUT_MS = 15_000;
const DISCONNECT_GRACE_MS = 5000;
const SPEECH_HOLD_MS = 900;
const NOTICE_MS = 6000;

const NO_CAPTIONS: LiveCaptions = { user: "", assistant: "" };

export function createLiveCallStore(options: {
  mint: LiveCallMint;
  handlers?: LiveCallHandlers;
  initialDeviceId?: string | null;
  platform?: LiveCallPlatform;
}): LiveCallStore {
  const { mint } = options;
  const platform = options.platform ?? browserLiveCallPlatform();
  const messages = { ...BROWSER_MESSAGES, ...platform.messages };
  const tickMs = platform.tickMs ?? TICK_MS;
  const log = platform.log ?? consoleLiveCallLog;
  let handlers: LiveCallHandlers = options.handlers ?? {
    delegate: () =>
      Promise.resolve({ kind: "failed", message: "the call has no chat" }),
    onCallEnd: () => undefined,
  };

  let state: LiveCallState = {
    phase: "idle",
    error: null,
    sessionId: null,
    startedAt: null,
    inputLevel: 0,
    outputLevel: 0,
    isMuted: false,
    inputDevices: [],
    inputDeviceId: options.initialDeviceId ?? null,
    captions: NO_CAPTIONS,
    delegatingQuery: null,
  };
  const listeners = new Set<() => void>();
  const set = (patch: Partial<LiveCallState>) => {
    let changed = false;
    for (const key of Object.keys(patch) as (keyof LiveCallState)[]) {
      if (!Object.is(state[key], patch[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) {
      return;
    }
    state = { ...state, ...patch };
    for (const listener of [...listeners]) {
      listener();
    }
  };
  const setCaptions = (patch: Partial<LiveCaptions>) => {
    const next = { ...state.captions, ...patch };
    if (
      next.user !== state.captions.user ||
      next.assistant !== state.captions.assistant
    ) {
      set({ captions: next });
    }
  };

  let current: Call | null = null;
  let muted = false;
  let deviceId: string | null = options.initialDeviceId ?? null;

  const send = (call: Call, command: LiveCommand) => {
    if (call.dc?.readyState === "open") {
      call.dc.send(JSON.stringify(command));
    }
  };
  const replayLeg = (call: Call) => {
    if (muted) {
      send(call, inputAudioMuteCommand(true));
    }
    for (const note of call.notes) {
      send(call, thinkingCommand(note));
    }
  };

  const refreshDevices = () => {
    platform
      .listInputDevices?.()
      .then((inputDevices) => set({ inputDevices }))
      .catch(() => undefined);
  };

  let noticeTimer: ReturnType<typeof setTimeout> | null = null;
  const clearNotice = () => {
    if (noticeTimer) {
      clearTimeout(noticeTimer);
      noticeTimer = null;
    }
  };
  /** A failure the call survives: shown for a while, then cleared. */
  const notify = (message: string) => {
    clearNotice();
    set({ error: message });
    noticeTimer = setTimeout(() => {
      noticeTimer = null;
      if (state.error === message && state.phase !== "errored") {
        set({ error: null });
      }
    }, NOTICE_MS);
  };

  const releaseStream = (stream: LiveCallStream | null) => {
    if (!stream) {
      return;
    }
    for (const track of stream.getTracks()) {
      track.stop();
    }
    platform.releaseMic?.(stream);
  };

  const finish = (
    call: Call,
    endReason: VoiceSessionEndReason,
    unloading: boolean
  ) => {
    if (call.ended) {
      return;
    }
    call.ended = true;
    call.minting.abort();
    call.runner?.dispose();
    call.loop?.dispose();
    if (call.tick) {
      clearInterval(call.tick);
    }
    if (call.startTimer) {
      clearTimeout(call.startTimer);
    }
    if (call.disconnectTimer) {
      clearTimeout(call.disconnectTimer);
    }
    const media = call.media;
    const closeMedia = () => media?.close();
    if (call.pc) {
      closeLeg(call.pc, call.dc, !unloading, closeMedia);
    } else {
      closeMedia();
    }
    releaseStream(call.stream);
    if (current === call) {
      current = null;
    }
    clearNotice();
    set({
      sessionId: null,
      startedAt: null,
      inputLevel: 0,
      outputLevel: 0,
      delegatingQuery: null,
      captions: NO_CAPTIONS,
      phase: endReason === "error" ? "errored" : "idle",
      ...(endReason === "error" ? {} : { error: null }),
    });

    handlers.onCallEnd({
      sessionId: call.sessionId,
      callId: call.callId,
      startedAt: call.startedAt,
      endedAt: new Date().toISOString(),
      endReason,
      turns: callTranscript(
        call.transcript.turns(),
        call.delegations.values(),
        call.sessionEpoch
      ),
      unloading,
    });
  };

  const fail = (call: Call, message: string) => {
    if (call.ended) {
      return;
    }
    set({ error: message });
    finish(call, "error", false);
  };

  const onTick = (call: Call) => {
    const levels = call.media?.levels(call.pc);
    if (levels) {
      set({
        inputLevel: muted ? 0 : levels.input,
        outputLevel: levels.output,
      });
    }
    if (!call.ready) {
      return;
    }
    const now = Date.now();
    const delegating = call.runner?.isBusy() ?? false;
    if (levels?.speaking) {
      call.lastAssistantSpeech = now;
    }
    if (call.swapping) {
      set({ phase: "switching" });
    } else if (now - call.lastUserSpeech < SPEECH_HOLD_MS) {
      set({ phase: "user-speaking" });
    } else if (now - call.lastAssistantSpeech < SPEECH_HOLD_MS) {
      set({ phase: "speaking" });
    } else if (delegating) {
      set({ phase: "delegating" });
    } else if (call.backendBusy) {
      set({ phase: "thinking" });
    } else {
      set({ phase: "listening" });
    }
    if (now - call.sessionEpoch > LIVE_CALL_MAX_MS) {
      finish(call, "limit", false);
      return;
    }
    if (delegating || call.backendBusy) {
      call.lastActivity = now;
    } else if (now - call.lastActivity > LIVE_CALL_IDLE_MS) {
      finish(call, "idle", false);
    }
  };

  const handleEvent = (call: Call, event: LiveEvent) => {
    switch (event.type) {
      case "session.started":
        if (call.startTimer) {
          clearTimeout(call.startTimer);
          call.startTimer = null;
        }
        call.ready = true;
        call.callId = event.sessionId || undefined;
        call.sessionEpoch = Date.now();
        call.lastActivity = call.sessionEpoch;
        call.media?.onLive?.();
        replayLeg(call);
        set({ phase: "listening" });
        handlers.onReady?.();
        send(call, commentaryCommand(call.greeting));
        return;
      case "transcript.delta": {
        const now = Date.now();
        call.lastActivity = now;
        call.transcript.push(
          event.speaker,
          event.delta,
          event.startMs + call.legStartMs
        );
        if (event.speaker === "user") {
          call.lastUserSpeech = now;
          const said = call.transcript.current("user");
          setCaptions({ user: said });
          if (
            call.runner?.isBusy() &&
            !call.cancelled &&
            CANCEL_INTENT.test(saidSince(said, call.saidAtDelegation))
          ) {
            call.cancelled = true;
            call.runner.cancel();
            handlers.onCancel?.();
          }
        } else {
          call.lastAssistantSpeech = now;
          setCaptions({ assistant: call.transcript.current("assistant") });
        }
        return;
      }
      case "delegation.created":
        call.backendBusy = true;
        return;
      case "backend":
        if (event.event.type === "response.created") {
          call.backendBusy = true;
        } else if (event.event.type === "response.finished") {
          call.backendBusy = false;
        }
        call.loop?.handle(event.event);
        return;
      case "error":
        log("warn", "session error", {
          code: event.code,
          message: event.message,
        });
        return;
      case "session.closed":
        if (!call.ended) {
          finish(call, "user", false);
        }
        return;
      default:
        return;
    }
  };

  const wireTools = (call: Call) => {
    const loop = createLiveFunctionCallLoop({
      send: (command) => send(call, command),
      knownFunctions: [MAGNUS_CHAT_FUNCTION],
      onCall: ({ callId, args }) => {
        call.runner
          ?.handle({
            toolCallId: callId,
            parameters:
              typeof args === "object" && args !== null
                ? (args as Record<string, unknown>)
                : {},
          })
          .catch(() => undefined);
      },
    });
    call.loop = loop;
    call.runner = createMagnusChatToolRunner({
      delegate: (query, signal, callContext) =>
        handlers.delegate(query, signal, callContext),
      sendResult: ({ toolCallId, output }) => loop.answer(toolCallId, output),
      callContext: () =>
        recentCallContext(
          call.transcript.turns(),
          [...call.delegations.values()].map((d) => d.atMs)
        ),
      onDelegation: (d) => {
        if (d.status === null) {
          call.cancelled = false;
          call.saidAtDelegation = call.transcript.current("user");
          call.activeQuery = d.query;
          call.delegations.set(d.toolCallId, {
            atMs: call.ready ? Date.now() - call.sessionEpoch : 0,
            marker: { query: d.query, status: "interrupted" },
          });
          set({ delegatingQuery: d.query });
          return;
        }
        call.activeQuery = null;
        const entry = call.delegations.get(d.toolCallId);
        if (entry) {
          entry.marker = {
            query: d.query,
            status: d.status,
            ...(d.messageId ? { messageId: d.messageId } : {}),
            ...(d.attempts && d.attempts > 1 ? { attempts: d.attempts } : {}),
          };
        }
        set({ delegatingQuery: null });
      },
    });
  };

  const attachLeg = (
    call: Call,
    pc: LiveCallPeerConnection,
    dc: LiveCallDataChannel
  ) => {
    pc.ontrack = (ev) => {
      const [remote] = ev.streams;
      if (remote && !call.ended) {
        call.media?.playRemote(remote);
      }
    };
    dc.onmessage = (ev) => {
      const event = parseLiveEvent(ev.data);
      if (event && !call.ended) {
        handleEvent(call, event);
      }
    };
    dc.onclose = () => fail(call, CALL_DROPPED_MESSAGE);
    pc.onconnectionstatechange = () => {
      if (call.ended) {
        return;
      }
      if (pc.connectionState === "failed") {
        fail(call, CALL_DROPPED_MESSAGE);
      } else if (pc.connectionState === "disconnected") {
        call.disconnectTimer ??= setTimeout(() => {
          call.disconnectTimer = null;
          if (pc.connectionState === "disconnected") {
            fail(call, CALL_DROPPED_MESSAGE);
          }
        }, DISCONNECT_GRACE_MS);
      } else if (call.disconnectTimer) {
        clearTimeout(call.disconnectTimer);
        call.disconnectTimer = null;
      }
    };
  };

  const mintSession = async (
    call: Call,
    pc: LiveCallPeerConnection,
    body: Omit<SdkLiveSessionRequest, "sdp" | "timeZone" | "callSessionId">
  ) => {
    await pc.setLocalDescription(await pc.createOffer());
    await waitForIceGathering(pc);
    const zone = timeZone();
    const res = await mint(
      {
        sdp: pc.localDescription?.sdp ?? "",
        callSessionId: call.sessionId,
        ...body,
        ...(zone ? { timeZone: zone } : {}),
      },
      { signal: call.minting.signal }
    );
    if (call.ended && res.ok) {
      handlers.onLateSession?.(call.sessionId);
    }
    return res;
  };

  const start: LiveCallStore["start"] = async (ctx) => {
    if (current) {
      return "started";
    }
    const call = newCall({
      sessionId: platform.newId(),
      chatId: ctx.chatId,
      voice: ctx.voice,
    });
    current = call;
    set({
      error: null,
      sessionId: call.sessionId,
      startedAt: call.startedAt,
      phase: "connecting",
    });

    try {
      call.media = platform.openMedia();

      const stream = await platform.acquireMic(deviceId);
      if (call.ended) {
        releaseStream(stream);
        return "failed";
      }
      call.stream = stream;
      const [track] = stream.getAudioTracks();
      if (!track) {
        throw Object.assign(new Error("No microphone found."), {
          name: "NotFoundError",
        });
      }
      track.enabled = !muted;
      refreshDevices();
      call.media.meterInput(stream);

      const pc = platform.createPeerConnection();
      call.pc = pc;
      call.sender = pc.addTrack(track, stream);
      const dc = pc.createDataChannel(LIVE_DATA_CHANNEL);
      call.dc = dc;
      attachLeg(call, pc, dc);
      wireTools(call);

      const res = await mintSession(call, pc, {
        voice: ctx.voice,
        ...(ctx.chatId ? { chatId: ctx.chatId } : {}),
      });
      if (call.ended) {
        return "failed";
      }
      if (!res.ok) {
        if (res.code === "VOICE_LIVE_UNCONFIGURED") {
          finish(call, "user", false);
          return "unconfigured";
        }
        if (res.code === "VOICE_LIVE_DISABLED") {
          finish(call, "user", false);
          return "disabled";
        }
        log("error", "session request failed", res);
        throw new Error(sessionErrorMessage(res, messages));
      }
      call.greeting = res.value.greeting;
      await pc.setRemoteDescription({ type: "answer", sdp: res.value.sdp });
      await call.media.activate?.();
      if (call.ended) {
        return "failed";
      }

      call.startTimer = setTimeout(() => {
        if (!call.ready) {
          fail(call, "The voice session didn’t start. Try again.");
        }
      }, SESSION_START_TIMEOUT_MS);
      call.tick = setInterval(() => onTick(call), tickMs);
      return "started";
    } catch (err) {
      if (!call.ended) {
        log("error", "start failed", err);
      }
      fail(call, startErrorMessage(err, messages));
      return "failed";
    }
  };

  const switchVoice: LiveCallStore["switchVoice"] = (voice) =>
    current
      ? switchCallVoice(current, voice, {
          platform,
          messages,
          log,
          sessionStartTimeoutMs: SESSION_START_TIMEOUT_MS,
          onCancel: () => handlers.onCancel?.(),
          set,
          notify,
          send,
          replayLeg,
          mintSession,
          attachLeg,
          wireTools,
        })
      : Promise.resolve(false);

  const pushContext: LiveCallStore["pushContext"] = (notes) => {
    const call = current;
    if (!call || call.ended) {
      return;
    }
    for (const note of notes) {
      if (!note.trim()) {
        continue;
      }
      call.notes.push(note);
      if (call.ready && !call.swapping) {
        send(call, thinkingCommand(note));
      }
    }
  };

  const stop: LiveCallStore["stop"] = (stopOpts) => {
    const call = current;
    if (call) {
      finish(call, stopOpts?.reason ?? "user", stopOpts?.unloading ?? false);
    } else {
      set({ phase: "idle", error: null });
    }
  };

  const setMuted: LiveCallStore["setMuted"] = (next) => {
    muted = next;
    set({ isMuted: next });
    const call = current;
    for (const track of call?.stream?.getAudioTracks() ?? []) {
      track.enabled = !next;
    }
    if (call?.ready) {
      send(call, inputAudioMuteCommand(next));
    }
  };

  const setInputDevice: LiveCallStore["setInputDevice"] = async (next) => {
    deviceId = next;
    set({ inputDeviceId: next });
    const call = current;
    if (!call?.sender) {
      return;
    }
    try {
      const stream = await platform.acquireMic(next);
      const [track] = stream.getAudioTracks();
      if (current !== call || call.ended || !track) {
        releaseStream(stream);
        return;
      }
      track.enabled = !muted;
      await call.sender?.replaceTrack(track);
      const previous = call.stream;
      call.stream = stream;
      call.media?.meterInput(stream);
      releaseStream(previous);
    } catch {
      notify("Couldn’t switch microphones.");
    }
  };

  let unwatchDevices: (() => void) | null = null;
  const watchDevices = (watch: boolean) => {
    if (watch && !unwatchDevices) {
      unwatchDevices =
        platform.watchInputDevices?.(refreshDevices) ?? (() => undefined);
    } else if (!watch && unwatchDevices) {
      unwatchDevices();
      unwatchDevices = null;
    }
  };

  return {
    dispose: () => {
      if (current) {
        finish(current, "user", true);
      }
      clearNotice();
      watchDevices(false);
    },
    getState: () => state,
    pushContext,
    setHandlers: (next) => {
      handlers = next;
    },
    setInputDevice,
    setMuted,
    start,
    stop,
    subscribe: (listener) => {
      listeners.add(listener);
      watchDevices(true);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          watchDevices(false);
        }
      };
    },
    switchVoice,
  };
}
